import { useState } from 'react';
import toast from 'react-hot-toast';
import type { PriceRule } from '@shared/types';
import { formatPriceRuleLabel, sortPriceRules } from '../lib/pricing';

interface PriceRulesEditorProps {
  rules: PriceRule[];
  basePriceCents: number;
  onChange: (rules: PriceRule[]) => void;
}

const RULE_TYPES: { value: PriceRule['type']; label: string }[] = [
  { value: 'season', label: 'Season / peak period' },
  { value: 'special_date', label: 'Special dates' },
];

export default function PriceRulesEditor({ rules, basePriceCents, onChange }: PriceRulesEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [type, setType] = useState<PriceRule['type']>('season');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [priceRands, setPriceRands] = useState('');

  const resetForm = () => {
    setEditingId(null);
    setLabel('');
    setType('season');
    setStartDate('');
    setEndDate('');
    setPriceRands('');
  };

  const loadRule = (rule: PriceRule) => {
    setEditingId(rule.id);
    setLabel(rule.label || '');
    setType(rule.type);
    setStartDate(rule.startDate);
    setEndDate(rule.endDate);
    setPriceRands((rule.price / 100).toString());
  };

  const saveRule = () => {
    if (!startDate || !endDate || !priceRands) {
      toast.error('Fill start date, end date, and price (Rands)');
      return;
    }
    if (endDate < startDate) {
      toast.error('End date must be on or after start date');
      return;
    }
    const rands = parseFloat(priceRands);
    if (Number.isNaN(rands) || rands < 0) {
      toast.error('Price must be a valid Rand amount');
      return;
    }

    const price = Math.round(rands * 100);
    const trimmedLabel = label.trim() || (type === 'season' ? 'Peak season' : 'Special rate');

    if (editingId) {
      onChange(
        rules.map((r) =>
          r.id === editingId
            ? { ...r, type, startDate, endDate, price, label: trimmedLabel }
            : r
        )
      );
      toast.success('Date range updated');
    } else {
      const rule: PriceRule = {
        id: `rule_${Date.now()}`,
        type,
        startDate,
        endDate,
        price,
        label: trimmedLabel,
      };
      onChange([...rules, rule]);
      toast.success('Date range added');
    }
    resetForm();
  };

  const removeRule = (id: string) => {
    onChange(rules.filter((r) => r.id !== id));
    if (editingId === id) resetForm();
  };

  const sorted = sortPriceRules(rules);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-700 mb-3">
          Set higher or lower nightly rates for specific date ranges (e.g. peak season, school
          holidays). These override the base price of{' '}
          <strong>R {(basePriceCents / 100).toFixed(2)}</strong> on the guest site and in booking
          totals.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Peak season, Dec holidays"
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as PriceRule['type'])} className="input">
              {RULE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Price / night (R)</label>
            <input
              type="number"
              min={0}
              step="1"
              value={priceRands}
              onChange={(e) => setPriceRands(e.target.value)}
              placeholder="e.g. 3500"
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={saveRule} className="btn btn-primary text-sm">
            {editingId ? 'Update date range' : 'Add date range'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn btn-secondary text-sm">
              Cancel edit
            </button>
          )}
        </div>
      </div>

      {sorted.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Dates</th>
                <th className="px-3 py-2">Rate / night</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((r) => (
                <tr key={r.id} className={editingId === r.id ? 'bg-blue-50' : 'bg-white'}>
                  <td className="px-3 py-2 font-medium text-gray-900">{formatPriceRuleLabel(r)}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.startDate} to {r.endDate}
                  </td>
                  <td className="px-3 py-2 text-gray-900">R {(r.price / 100).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => loadRule(r)}
                      className="text-[#32373c] hover:underline text-xs font-medium"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRule(r.id)}
                      className="text-red-600 hover:underline text-xs font-medium"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No date ranges yet. Add peak or off-peak pricing above.</p>
      )}
    </div>
  );
}
